"use client";

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";

import createId from "@/lib/create-id";

import type { CarePlanCycleDetail } from "@/types/care-plan";

import {
  type CarePlanBranch,
  type CarePlanStep,
  CarePlanStepStatus,
  type DragOverMoveInfo,
  type DragOverMoveParams,
  type DuplicatedCycleInfo,
  StepType,
  type TemplateStep,
  buildBranchIdMap,
  findSelfContainedCycles,
  findStep,
  findStepInsertIndex,
  isLinkedMessage,
  isPatientStep,
  locateCycleInStep,
  moveStepBetweenContainers,
  reconcileCycleScope,
  remapCycleIds,
  removeCyclesAndReparent,
  removeStepFromTree,
  resolveDragOverMove,
  updateBranchInTree,
  updateContainerSteps,
  updateStepInTree,
} from "./types";

/**
 * Recursively deep-clone a step, assigning fresh IDs to the step itself
 * and all nested branches / sub-steps. Updates parentStepId and
 * decisionStepId references so the cloned subtree is fully independent.
 */
function deepCloneStep<T extends CarePlanStep>(step: T, newStepId?: string): T {
  const id = newStepId ?? createId("cps");
  const cloned = {
    ...step,
    id,
    measurements: step.measurements.map((m) => ({ ...m, id: undefined })),
  } as T;

  if (step.type === StepType.DECISION && step.branches) {
    cloned.branches = step.branches.map((branch) => {
      const newBranchId = createId("cpb");
      return {
        ...branch,
        id: newBranchId,
        decisionStepId: id,
        steps: branch.steps.map((subStep) => {
          const clonedSub = deepCloneStep(subStep);
          if (clonedSub.branchId) {
            return { ...clonedSub, branchId: newBranchId };
          }
          return clonedSub;
        }),
      };
    });

    // Fix parentStepId references within each cloned branch.
    // Build a mapping from old step IDs to new step IDs.
    for (let bIdx = 0; bIdx < (step.branches ?? []).length; bIdx++) {
      const origBranch = step.branches![bIdx];
      const clonedBranch = cloned.branches![bIdx];
      const idMap = new Map<string, string>();
      for (let sIdx = 0; sIdx < origBranch.steps.length; sIdx++) {
        idMap.set(origBranch.steps[sIdx].id, clonedBranch.steps[sIdx].id);
      }
      clonedBranch.steps = clonedBranch.steps.map((s) => {
        if (s.parentStepId && idMap.has(s.parentStepId)) {
          return { ...s, parentStepId: idMap.get(s.parentStepId)! };
        }
        return s;
      });
    }
  }

  return cloned;
}

function createDefaultBranches(decisionStepId: string): CarePlanBranch[] {
  return [
    {
      id: createId("cpb"),
      decisionStepId,
      position: 0,
      name: "Option A",
      tagIds: [],
      steps: [],
    },
    {
      id: createId("cpb"),
      decisionStepId,
      position: 1,
      name: "Option B",
      tagIds: [],
      steps: [],
    },
  ];
}

function createDefaultStep(
  stepNumber: number,
  type: StepType = StepType.APPOINTMENT,
): TemplateStep {
  const id = createId("cps");
  return {
    kind: "template",
    id,
    stepNumber,
    type,
    serviceIds: [],
    medicationIds: [],
    measurements: [],
    minutesAfterPrevious: 0,
    ...(type === StepType.DECISION && {
      branches: createDefaultBranches(id),
    }),
  };
}

function recalculatePositions(steps: CarePlanStep[]): CarePlanStep[] {
  return steps.map((step, i) => ({ ...step, stepNumber: i + 1 }));
}

function applyMoveResult(
  prev: CarePlanStep[],
  params: DragOverMoveParams,
): CarePlanStep[] {
  const result = moveStepBetweenContainers(prev, params);
  if (result.steps === prev) return prev;
  let next = result.steps;
  if (result.movedFromContainer) {
    next = updateContainerSteps(
      next,
      result.movedFromContainer,
      recalculatePositions,
    );
  }
  if (
    result.movedToContainer &&
    result.movedToContainer !== result.movedFromContainer
  ) {
    next = updateContainerSteps(
      next,
      result.movedToContainer,
      recalculatePositions,
    );
  }
  return next;
}

const NOOP_SET_STEPS: Dispatch<SetStateAction<CarePlanStep[]>> = () => {};

export function useCarePlan(
  steps: CarePlanStep[],
  setSteps?: Dispatch<SetStateAction<CarePlanStep[]>>,
  options?: {
    cycles?: CarePlanCycleDetail[];
    setCycles?: Dispatch<SetStateAction<CarePlanCycleDetail[]>>;
    onCyclesDuplicated?: (info: DuplicatedCycleInfo[]) => void;
  },
) {
  const stableSetSteps = setSteps ?? NOOP_SET_STEPS;
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const selectedStep = selectedStepId ? findStep(steps, selectedStepId) : null;

  const addStep = useCallback(
    (type?: StepType) => {
      const newStep = createDefaultStep(0, type);
      stableSetSteps((prev) => [
        ...prev,
        { ...newStep, stepNumber: prev.length + 1 },
      ]);
      setSelectedStepId(newStep.id);
    },
    [stableSetSteps],
  );

  const duplicateStep = useCallback(
    (step: CarePlanStep, atTimelineIndex: number) => {
      // DFS: find all cycles self-contained within this step (including branch descendants)
      const selfContained = options?.cycles?.length
        ? findSelfContainedCycles(step, steps, options.cycles)
        : [];

      const cycleIdMap = new Map<string, string>();
      const pendingLocations: Array<{
        cycle: CarePlanCycleDetail;
        loc: ReturnType<typeof locateCycleInStep>;
      }> = [];
      for (const cycle of selfContained) {
        const newId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        cycleIdMap.set(cycle.id, newId);
        pendingLocations.push({
          cycle: { ...cycle, id: newId },
          loc: locateCycleInStep(step, cycle.id),
        });
      }
      // Remap parentCycleId so child cycles point to the new parent
      for (const entry of pendingLocations) {
        if (
          entry.cycle.parentCycleId &&
          cycleIdMap.has(entry.cycle.parentCycleId)
        ) {
          entry.cycle = {
            ...entry.cycle,
            parentCycleId: cycleIdMap.get(entry.cycle.parentCycleId)!,
          };
        }
      }

      let newStep = deepCloneStep(step);
      if (cycleIdMap.size > 0) {
        newStep = remapCycleIds(newStep, cycleIdMap);
      }

      // Build old→new branch ID mapping so we can resolve branchIds for the new cycles
      const branchIdMap = buildBranchIdMap(step, newStep);

      const duplicatedCycles: DuplicatedCycleInfo[] = [];
      for (const { cycle, loc } of pendingLocations) {
        if (!loc) continue;
        // Top-level cycle (on the step itself): use the new step's ID
        if (loc.branchId === null) {
          duplicatedCycles.push({
            cycle,
            fromStepId: newStep.id,
            toStepId: newStep.id,
            branchId: null,
          });
        } else {
          // Branch-scoped cycle: resolve step IDs from the cloned branches
          const newBranchId = branchIdMap.get(loc.branchId) ?? loc.branchId;
          const branch = newStep.branches?.find((b) => b.id === newBranchId);
          const timeline = branch
            ? branch.steps.filter((s) => !isLinkedMessage(s))
            : [];
          const fromId = timeline[loc.fromIndex]?.id;
          const toId = timeline[loc.toIndex]?.id;
          if (fromId && toId) {
            duplicatedCycles.push({
              cycle,
              fromStepId: fromId,
              toStepId: toId,
              branchId: newBranchId,
            });
          }
        }
      }

      stableSetSteps((prev) => {
        const children = prev.filter(
          (s) => isLinkedMessage(s) && s.parentStepId === step.id,
        );
        const newChildMessages: CarePlanStep[] = children.map((c) => ({
          ...c,
          id: createId("cps"),
          parentStepId: newStep.id,
        }));
        const insertAt = findStepInsertIndex(prev, atTimelineIndex);
        const toInsert = [newStep, ...newChildMessages];
        const next = [
          ...prev.slice(0, insertAt),
          ...toInsert,
          ...prev.slice(insertAt),
        ];
        return recalculatePositions(next);
      });

      if (duplicatedCycles.length > 0) {
        options?.onCyclesDuplicated?.(duplicatedCycles);
      }

      setSelectedStepId(newStep.id);
    },
    [stableSetSteps, steps, options?.cycles, options?.onCyclesDuplicated],
  );

  const updateStep = useCallback(
    (id: string, updates: Partial<Omit<CarePlanStep, "kind">>) => {
      stableSetSteps((prev) => updateStepInTree(prev, id, updates));
    },
    [stableSetSteps],
  );

  const removeStep = useCallback(
    (id: string) => {
      stableSetSteps((prev) => {
        const stepToRemove = findStep(prev, id);
        // Don't allow removing completed steps
        if (
          stepToRemove &&
          isPatientStep(stepToRemove) &&
          stepToRemove.status === CarePlanStepStatus.COMPLETED
        ) {
          return prev;
        }
        // Remove step from anywhere in the tree (top-level or branch)
        const updated = removeStepFromTree(prev, id);
        return updated === prev ? prev : recalculatePositions(updated);
      });
      setSelectedStepId((curr) => (curr === id ? null : curr));
      setExpandedStepId((curr) => (curr === id ? null : curr));
    },
    [stableSetSteps],
  );

  const selectStep = useCallback((id: string | null) => {
    setSelectedStepId(id);
  }, []);

  const toggleStepExpanded = useCallback((id: string) => {
    setExpandedStepId((prev) => (prev === id ? null : id));
  }, []);

  /**
   * Move a timeline step (and its child messages) from one position to another.
   * Indices refer to positions in the filtered non-message step list.
   */
  const moveStep = useCallback(
    (fromIndex: number, toIndex: number) => {
      stableSetSteps((prev) => {
        const timeline = prev.filter((s) => !isLinkedMessage(s));

        if (
          fromIndex < 0 ||
          fromIndex >= timeline.length ||
          toIndex < 0 ||
          toIndex >= timeline.length ||
          fromIndex === toIndex
        ) {
          return prev;
        }

        // Reorder timeline steps
        const reordered = [...timeline];
        const [removed] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, removed);

        // Rebuild full array: each timeline step followed by its child messages
        const childrenMap = new Map<string, CarePlanStep[]>();
        for (const s of prev) {
          if (isLinkedMessage(s)) {
            const children = childrenMap.get(s.parentStepId) ?? [];
            children.push(s);
            childrenMap.set(s.parentStepId, children);
          }
        }

        const result: CarePlanStep[] = [];
        for (const step of reordered) {
          result.push(step);
          const children = childrenMap.get(step.id);
          if (children) result.push(...children);
        }

        return recalculatePositions(result);
      });
    },
    [stableSetSteps],
  );

  const restoreSteps = useCallback(
    (snapshot: CarePlanStep[]) => {
      stableSetSteps(snapshot);
    },
    [stableSetSteps],
  );

  const cyclesRef = options?.cycles;
  const setCyclesRef = options?.setCycles;

  /**
   * Apply a move and re-scope cycle membership against the latest cycles list.
   * Called from both `moveStepTo` (drag-end commits) and `applyDragOverMove`
   * (per-frame dragover). Stale `cycleId`s left behind by a step exiting its
   * cycle's container — and any cycles that empty out as a result — are
   * stripped/dropped here.
   */
  function applyMoveAndReconcile(
    prev: CarePlanStep[],
    move: DragOverMoveParams,
  ): { steps: CarePlanStep[]; removedCycleIds: string[] } {
    const moved = applyMoveResult(prev, move);
    if (moved === prev || !cyclesRef || cyclesRef.length === 0) {
      return { steps: moved, removedCycleIds: [] };
    }
    return reconcileCycleScope(prev, moved, cyclesRef);
  }

  /**
   * Unified DnD move: same-container reorder and cross-container moves
   * (root↔branch, branch↔branch). Replaces moveStep / moveBranchStep at
   * the drag layer.
   */
  const moveStepTo = useCallback(
    (params: DragOverMoveParams) => {
      let removed: string[] = [];
      stableSetSteps((prev) => {
        const r = applyMoveAndReconcile(prev, params);
        removed = r.removedCycleIds;
        return r.steps;
      });
      if (removed.length > 0 && setCyclesRef) {
        setCyclesRef((prev) => removeCyclesAndReparent(prev, removed));
      }
    },
    // applyMoveAndReconcile closes over cyclesRef/setCyclesRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableSetSteps, cyclesRef, setCyclesRef],
  );

  const applyDragOverMove = useCallback(
    (info: DragOverMoveInfo) => {
      let removed: string[] = [];
      stableSetSteps((prev) => {
        const move = resolveDragOverMove(prev, info);
        if (!move) return prev;
        const r = applyMoveAndReconcile(prev, move);
        removed = r.removedCycleIds;
        return r.steps;
      });
      if (removed.length > 0 && setCyclesRef) {
        setCyclesRef((prev) => removeCyclesAndReparent(prev, removed));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableSetSteps, cyclesRef, setCyclesRef],
  );

  const addBranch = useCallback(
    (decisionStepId: string) => {
      stableSetSteps((prev) => {
        const target = findStep(prev, decisionStepId);
        if (!target) return prev;
        const branches = target.branches ?? [];
        const nextPosition = branches.length;
        const label = String.fromCharCode(65 + nextPosition); // A, B, C, ...
        const newBranch: CarePlanBranch = {
          id: createId("cpb"),
          decisionStepId,
          position: nextPosition,
          name: `Option ${label}`,
          tagIds: [],
          steps: [],
        };
        return updateStepInTree(prev, decisionStepId, {
          branches: [...branches, newBranch],
        });
      });
    },
    [stableSetSteps],
  );

  const removeBranch = useCallback(
    (decisionStepId: string, branchId: string) => {
      stableSetSteps((prev) => {
        const target = findStep(prev, decisionStepId);
        if (!target) return prev;
        const branches = (target.branches ?? [])
          .filter((b) => b.id !== branchId)
          .map((b, i) => ({ ...b, position: i }));
        return updateStepInTree(prev, decisionStepId, { branches });
      });
    },
    [stableSetSteps],
  );

  const addBranchStep = useCallback(
    (branchId: string, type?: StepType) => {
      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => {
          const newStep = createDefaultStep(branch.steps.length + 1, type);
          return {
            ...branch,
            steps: recalculatePositions([...branch.steps, newStep]),
          };
        }),
      );
    },
    [stableSetSteps],
  );

  const removeBranchStep = useCallback(
    (branchId: string, stepId: string) => {
      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => ({
          ...branch,
          steps: recalculatePositions(
            branch.steps.filter(
              (s) => s.id !== stepId && s.parentStepId !== stepId,
            ),
          ),
        })),
      );
    },
    [stableSetSteps],
  );

  const moveBranchStep = useCallback(
    (branchId: string, fromIndex: number, toIndex: number) => {
      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => {
          const timeline = branch.steps.filter((s) => !isLinkedMessage(s));

          if (
            fromIndex < 0 ||
            fromIndex >= timeline.length ||
            toIndex < 0 ||
            toIndex >= timeline.length ||
            fromIndex === toIndex
          ) {
            return branch;
          }

          const reordered = [...timeline];
          const [removed] = reordered.splice(fromIndex, 1);
          reordered.splice(toIndex, 0, removed);

          const childrenMap = new Map<string, CarePlanStep[]>();
          for (const s of branch.steps) {
            if (isLinkedMessage(s)) {
              const children = childrenMap.get(s.parentStepId) ?? [];
              children.push(s);
              childrenMap.set(s.parentStepId, children);
            }
          }

          const result: CarePlanStep[] = [];
          for (const step of reordered) {
            result.push(step);
            const children = childrenMap.get(step.id);
            if (children) result.push(...children);
          }

          return { ...branch, steps: recalculatePositions(result) };
        }),
      );
    },
    [stableSetSteps],
  );

  const updateBranchStep = useCallback(
    (branchId: string, stepId: string, updates: Partial<CarePlanStep>) => {
      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => ({
          ...branch,
          steps: branch.steps.map((s) =>
            s.id === stepId ? ({ ...s, ...updates } as typeof s) : s,
          ),
        })),
      );
    },
    [stableSetSteps],
  );

  const duplicateBranchStep = useCallback(
    (branchId: string, step: CarePlanStep, atTimelineIndex: number) => {
      // DFS: find all cycles self-contained within this step
      const selfContained = options?.cycles?.length
        ? findSelfContainedCycles(step, steps, options.cycles)
        : [];

      const cycleIdMap = new Map<string, string>();
      const pendingLocations: Array<{
        cycle: CarePlanCycleDetail;
        loc: ReturnType<typeof locateCycleInStep>;
      }> = [];
      for (const cycle of selfContained) {
        const newId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        cycleIdMap.set(cycle.id, newId);
        pendingLocations.push({
          cycle: { ...cycle, id: newId },
          loc: locateCycleInStep(step, cycle.id),
        });
      }
      // Remap parentCycleId so child cycles point to the new parent
      for (const entry of pendingLocations) {
        if (
          entry.cycle.parentCycleId &&
          cycleIdMap.has(entry.cycle.parentCycleId)
        ) {
          entry.cycle = {
            ...entry.cycle,
            parentCycleId: cycleIdMap.get(entry.cycle.parentCycleId)!,
          };
        }
      }

      let newStep = deepCloneStep(step);
      if (cycleIdMap.size > 0) {
        newStep = remapCycleIds(newStep, cycleIdMap);
      }

      const branchIdMap = buildBranchIdMap(step, newStep);

      const duplicatedCycles: DuplicatedCycleInfo[] = [];
      for (const { cycle, loc } of pendingLocations) {
        if (!loc) continue;
        if (loc.branchId === null) {
          // Cycle on the step itself — use the new step's ID
          duplicatedCycles.push({
            cycle,
            fromStepId: newStep.id,
            toStepId: newStep.id,
            branchId,
          });
        } else {
          // Branch-scoped cycle: resolve step IDs from the cloned branches
          const newBranchId = branchIdMap.get(loc.branchId) ?? loc.branchId;
          const branch = newStep.branches?.find((b) => b.id === newBranchId);
          const timeline = branch
            ? branch.steps.filter((s) => !isLinkedMessage(s))
            : [];
          const fromId = timeline[loc.fromIndex]?.id;
          const toId = timeline[loc.toIndex]?.id;
          if (fromId && toId) {
            duplicatedCycles.push({
              cycle,
              fromStepId: fromId,
              toStepId: toId,
              branchId: newBranchId,
            });
          }
        }
      }

      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => {
          const children = branch.steps.filter(
            (s) => isLinkedMessage(s) && s.parentStepId === step.id,
          );
          const newChildMessages: CarePlanStep[] = children.map((c) => ({
            ...c,
            id: createId("cps"),
            parentStepId: newStep.id,
          }));
          const insertAt = findStepInsertIndex(branch.steps, atTimelineIndex);
          const toInsert = [newStep, ...newChildMessages];
          const next = [
            ...branch.steps.slice(0, insertAt),
            ...toInsert,
            ...branch.steps.slice(insertAt),
          ];
          return { ...branch, steps: recalculatePositions(next) };
        }),
      );

      if (duplicatedCycles.length > 0) {
        options?.onCyclesDuplicated?.(duplicatedCycles);
      }

      setSelectedStepId(newStep.id);
    },
    [stableSetSteps, steps, options?.cycles, options?.onCyclesDuplicated],
  );

  // Branches aren't selected in template mode
  const updateBranch = useCallback(
    (
      branchId: string,
      updates: Partial<Pick<CarePlanBranch, "name" | "tagIds">>,
    ) => {
      stableSetSteps((prev) =>
        updateBranchInTree(prev, branchId, (branch) => ({
          ...branch,
          ...updates,
        })),
      );
    },
    [stableSetSteps],
  );

  const selectBranch = useCallback(
    async (_decisionStepId: string, _branchId: string) => {
      // No-op for template mode
    },
    [],
  );

  return {
    steps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    addStep,
    duplicateStep,
    updateStep,
    removeStep,
    selectStep,
    moveStep,
    moveStepTo,
    applyDragOverMove,
    restoreSteps,
    toggleStepExpanded,
    addBranch,
    removeBranch,
    updateBranch,
    addBranchStep,
    removeBranchStep,
    moveBranchStep,
    updateBranchStep,
    duplicateBranchStep,
    selectBranch,
  };
}
